import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { Fit } from "../../shared/types.ts";
import { Button, Chip, MarkLoader, cn, softSpring, spring, staggerParent, rise } from "../ui/index.ts";
import { ApiError, getFit } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";

let noModel = false;
const modelListeners = new Set<() => void>();
const fits = new Map<string, Fit>();
const asking = new Map<string, Promise<Fit>>();

/** One request per (user, job) for the session, shared by every panel that asks while it is in flight. */
function fetchFit(key: string, jobId: string): Promise<Fit> {
  let p = asking.get(key);
  if (!p) {
    p = getFit(jobId).then(
      (fit) => {
        fits.set(key, fit);
        asking.delete(key);
        return fit;
      },
      (e) => {
        asking.delete(key);
        throw e;
      },
    );
    asking.set(key, p);
  }
  return p;
}

/** False once the API says there is no model; every "Why it fits" then hides for the session. */
function useModel(): boolean {
  return useSyncExternalStore(
    (cb) => {
      modelListeners.add(cb);
      return () => modelListeners.delete(cb);
    },
    () => !noModel,
    () => true,
  );
}

type FitState = { state: "idle" } | { state: "loading" } | { state: "error" } | { state: "ready"; fit: Fit };

function useFit(jobId: string) {
  const key = `${useApp().user.id}\u0000${jobId}`;
  const [s, setS] = useState<FitState>(() => {
    const f = fits.get(key);
    return f ? { state: "ready", fit: f } : asking.has(key) ? { state: "loading" } : { state: "idle" };
  });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const load = useCallback(() => {
    const cached = fits.get(key);
    if (cached) return setS({ state: "ready", fit: cached });
    setS({ state: "loading" });
    fetchFit(key, jobId).then(
      (fit) => alive.current && setS({ state: "ready", fit }),
      (e) => {
        if (e instanceof ApiError && e.status === 404 && e.error === "no_model") {
          noModel = true;
          for (const l of modelListeners) l();
        }
        if (alive.current) setS({ state: "error" });
      },
    );
  }, [key, jobId]);
  useEffect(() => {
    if (s.state === "loading" && asking.has(key)) load();
  }, []);
  return [s, load] as const;
}

/** "Why it fits": a button that loads the model's read inline, or loads it right away with `auto`. Hidden without a model. */
export function FitPanel({ jobId, auto = false }: { jobId: string; auto?: boolean }) {
  const model = useModel();
  const [s, load] = useFit(jobId);
  useEffect(() => {
    if (auto && model && s.state === "idle") load();
  }, [auto, model, s.state, load]);
  if (!model) return null;
  if (s.state === "idle")
    return (
      <Button variant="tonal" size="sm" className="self-start" onClick={load}>
        Why it fits
      </Button>
    );
  return (
    <motion.section
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={softSpring}
      className="flex flex-col gap-3 rounded-card bg-primary-soft/70 p-4 ring-1 ring-primary-tint ring-inset"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-bold tracking-[-0.01em] text-ink">Why it fits</h3>
        <AnimatePresence>
          {s.state === "ready" && (
            <motion.span initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={spring} className="ml-auto">
              <Chip tone={s.fit.score >= 70 ? "ok" : s.fit.score >= 40 ? "brand" : "bad"}>
                <span className="tabular-nums">{Math.round(s.fit.score)}%</span> match
              </Chip>
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      {s.state === "loading" ? (
        <MarkLoader layout="row" label="Reading the role against your profile" />
      ) : s.state === "error" ? (
        <p className="text-sm text-ink-soft">
          That didn't load.{" "}
          <button type="button" className="font-semibold text-primary-ink underline-offset-2 hover:underline" onClick={load}>
            Try again
          </button>
        </p>
      ) : (
        <FitBody fit={s.fit} />
      )}
    </motion.section>
  );
}

function FitBody({ fit }: { fit: Fit }) {
  return (
    <motion.div variants={staggerParent(0.05)} initial="hidden" animate="show" className="flex flex-col gap-2.5">
      <motion.p variants={rise} className="text-sm leading-relaxed text-ink-soft text-pretty">
        {fit.summary}
      </motion.p>
      {fit.pros.map((p) => (
        <Point key={`p${p}`} good text={p} />
      ))}
      {fit.cons.map((c) => (
        <Point key={`c${c}`} text={c} />
      ))}
    </motion.div>
  );
}

function Point({ good = false, text }: { good?: boolean; text: string }) {
  return (
    <motion.p variants={rise} className="flex gap-2.5 text-sm text-ink">
      <span
        aria-hidden
        className={cn(
          "mt-px grid size-[18px] shrink-0 place-items-center rounded-chip text-[12px] font-bold leading-none",
          good ? "bg-tone-mint text-tone-mint-ink" : "bg-tone-red text-tone-red-ink",
        )}
      >
        {good ? "+" : "−"}
      </span>
      <span className="text-pretty">
        <span className="sr-only">{good ? "In favour: " : "Against: "}</span>
        {text}
      </span>
    </motion.p>
  );
}
